/**
 * Runtime'owa sanitizacja typografii w DOM.
 *
 * Łapie treści, które nie przechodzą przez moduły `src/content/*`:
 * wpisy redakcyjne, dane z CMS/bazy, teksty wstawiane przez formularze
 * i komponenty firm trzecich. Dzięki temu myślniki AI nie pojawią się
 * nigdzie na stronie.
 */
import { polishTypography, sanitizeDashes } from "@/lib/typography";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);

function shouldSkip(node: Node): boolean {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName) || el.hasAttribute("data-no-dash-sanitize")) return true;
    el = el.parentElement;
  }
  return false;
}

function sanitizeTextNode(node: Text): void {
  const value = node.nodeValue;
  if (!value || !value.trim()) return;
  if (shouldSkip(node)) return;
  const next = polishTypography(sanitizeDashes(value));
  if (next !== value) node.nodeValue = next;
}

function sanitizeTree(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    sanitizeTextNode(root as Text);
    return;
  }
  if (!(root instanceof Element) && !(root instanceof Document)) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const found: Text[] = [];
  while (walker.nextNode()) found.push(walker.currentNode as Text);
  for (const node of found) sanitizeTextNode(node);
}

let observer: MutationObserver | null = null;

/** Włącza obserwator DOM; zwraca funkcję czyszczącą. */
export function installDashSanitizer(): () => void {
  if (typeof document === "undefined") return () => {};

  const start = () => {
    sanitizeTree(document.body);

    observer?.disconnect();
    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "characterData") {
          sanitizeTextNode(mutation.target as Text);
        } else {
          mutation.addedNodes.forEach((node) => sanitizeTree(node));
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  // Czekamy na koniec hydracji Reacta, żeby nie modyfikować węzłów,
  // które React dopiero dopasowuje (unika hydration mismatch).
  const schedule = () => {
    setTimeout(() => requestAnimationFrame(start), 400);
  };
  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }

  return () => {
    observer?.disconnect();
    observer = null;
  };
}
